/**
 * Charades cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const charadesCardsPack: ToilePack = {
    slug: 'charades-cards',
    category: 'tabletop',
    name: 'Charades cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'actions', label: 'Action cards', subject: 'a printable sheet of eight charades cards in a 2 by 4 grid with dashed cut lines, reading "Brushing your teeth", "Riding a bike", "Flying a kite" and five more everyday actions'},
        {key: 'animals', label: 'Animal cards', subject: 'a printable sheet of eight charades cards in a 2 by 4 grid with dashed cut lines, each with a small animal icon and its name, "Elephant", "Penguin", "Kangaroo" and five more'},
        {key: 'blank', label: 'Blank cards', subject: 'a printable sheet of eight blank charades cards in a 2 by 4 grid with dashed cut lines, each headed "Charades" with a small theatre-mask icon and an empty line'},
    ],
    styles: [
        {key: 'party', label: 'Party fun', style: 'a bold party printable with confetti corners and chunky rounded lettering', palette: 'bright magenta, teal and yellow on white', framing: 'cards evenly spaced on the page with a wide margin'},
        {key: 'theatre', label: 'Theatre red', style: 'a vintage theatre-playbill look with curtain-red borders and gold lettering', palette: 'deep red and gold on cream', framing: 'cards evenly spaced on the page with a wide margin'},
        {key: 'cutline', label: 'Simple outline', style: 'clean black outline cards with thin borders and small line icons', palette: 'black ink only', framing: 'cards evenly spaced on the page with a wide margin'},
    ],
    directions: ['movie titles', 'christmas', 'for toddlers'],
    directionHint: 'try: movie titles',
    seo: {
        title: 'Charades cards: action, animal and blank cards',
        metaDescription: 'Printable charades cards in three styles: a sheet of everyday action cards, an animal card sheet and a blank set to write your own.',
        h1: 'Charades cards: action cards, animal cards, blank cards',
        tagline: 'Nine sheets: action cards, animal cards and blank cards, in three styles.',
        intro: 'A family or a class prints a sheet, cuts the cards and plays charades at a party. This pack shows a sheet of eight action cards such as Riding a bike, a sheet of eight animal cards, and a sheet of blank cards to write your own. Each comes as a bold party style, a vintage theatre look or a simple black outline. Type a theme, such as movie titles, and the sheet keeps its grid.',
        useCases: [
            'a party game for a group of guests',
            'a rainy-afternoon game for children',
            'a class game with a set of cards',
            'a blank set with words of your own',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: action cards, animal cards, and a set of blank cards.'},
            {q: 'How many cards are on each sheet?', a: 'Eight, in a grid of two by four, with dashed cut lines between them.'},
            {q: 'Can I write my own actions?', a: 'Yes. The blank sheet has eight cards, each with an empty line under the heading Charades.'},
            {q: 'Which style is easiest to print?', a: 'Simple outline uses black ink only, with thin borders and small line icons.'},
        ],
    },
}
